/**
 * Central config from process.env.REACT_APP_* only.
 * Set .env.development (local) and .env.production (live) per environment.
 *
 * Socket / Node API (REACT_APP_NODE_SERVER_URL):
 * - Local: set to http://localhost:5000 or http://YOUR_IP:5000 (with port).
 * - Production: set to https://your-api-domain.com with NO port (same host or Nginx proxy).
 */
const trim = (v) => (v && typeof v === 'string' ? v.trim().replace(/\/$/, '') : '');

function nodeSocketFallback() {
  if (typeof window === 'undefined') return '';
  const { hostname, protocol } = window.location;
  const port = process.env.REACT_APP_SOCKET_SERVER_PORT || '5000';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:${port}`;
  }
  // `npm start` serves the app from the CRA dev server (:3000), never from the Node server.
  if (process.env.NODE_ENV === 'development') {
    return `${protocol}//${hostname}:${port}`;
  }
  // Production fallback: same origin, no port (avoid :5000 in production)
  return `${protocol}//${hostname}`;
}

const nodeBase =
  trim(process.env.REACT_APP_NODE_SERVER_URL) ||
  trim(process.env.REACT_APP_SOCKET_SERVER_URL) ||
  nodeSocketFallback();


export const ENV = {
  PHP_API_BASE: trim(process.env.REACT_APP_PHP_BASE_URL) || '',
  NODE_API_BASE: nodeBase,
  AI_API_BASE: trim(process.env.REACT_APP_AI_SERVER_URL) || '',
  SOCKET_URL: nodeBase,
  SOCKET_PORT: process.env.REACT_APP_SOCKET_SERVER_PORT || '5000',
  QUESTION_PAPER_BASE: trim(process.env.REACT_APP_QUESTION_PAPER_BASE_URL) || '',
  REQUIRED_CAMERAS: Math.max(1, parseInt(process.env.REACT_APP_REQUIRED_CAMERAS || '1', 10)),
  /** Recording chunks waiting for upload behind the in-flight one (oldest dropped beyond these caps). */
  RECORDING_QUEUE_MAX_CHUNKS: Math.max(1, parseInt(process.env.REACT_APP_RECORDING_QUEUE_MAX_CHUNKS || '5', 10)),
  RECORDING_QUEUE_MAX_MB: Math.max(1, parseInt(process.env.REACT_APP_RECORDING_QUEUE_MAX_MB || '40', 10)),
  /** Upload chunks straight to S3 with presigned URLs (needs bucket CORS); false = always through the Node server. */
  RECORDING_DIRECT_S3: (process.env.REACT_APP_RECORDING_DIRECT_S3 || 'true').trim().toLowerCase() !== 'false',
  /** The same violation type is reported (socket, image upload, DB) at most once per interval. 0 = every detection. */
  VIOLATION_REPEAT_INTERVAL_SEC: Math.max(0, parseInt(process.env.REACT_APP_VIOLATION_REPEAT_INTERVAL_SEC ?? '30', 10) || 0),
  /** Liveness: per-request timeout, random delay before the first scan (spreads a mass start), max backoff after AI failures. */
  LIVENESS_TIMEOUT_MS: Math.max(1000, parseInt(process.env.REACT_APP_LIVENESS_TIMEOUT_MS || '20000', 10)),
  LIVENESS_START_JITTER_MS: Math.max(0, parseInt(process.env.REACT_APP_LIVENESS_START_JITTER_MS ?? '3000', 10) || 0),
  LIVENESS_MAX_BACKOFF_MS: Math.max(2000, parseInt(process.env.REACT_APP_LIVENESS_MAX_BACKOFF_MS || '16000', 10)),
  /** Random delay before a student's first monitoring check (default = one 2 s monitoring cycle). */
  MONITORING_START_JITTER_MS: Math.max(0, parseInt(process.env.REACT_APP_MONITORING_START_JITTER_MS ?? '2000', 10) || 0),
  /** Interval in ms between exam AI monitoring checks (default: 5000ms = 5s to avoid CPU saturation). */
  MONITORING_INTERVAL_MS: Math.max(1000, parseInt(process.env.REACT_APP_MONITORING_INTERVAL_MS || '5000', 10)),
  /** Set to false to disable video recording and saving to S3 (env: REACT_APP_RECORDING_TO_S3_ENABLED=false) */
};
